import React, { useEffect, useState, useRef } from "react";
import { Container, Tab, Nav, Table, Form, Button } from "react-bootstrap";
import axios from "axios";
import { API_BASE_URL } from '../config/api';

const Trending = () => {
  const [mode, setMode] = useState("top");
  const [hashtag, setHashtag] = useState("ice");
  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [spyUsername, setSpyUsername] = useState('');
  const [spyLoading, setSpyLoading] = useState(false);
  const [competitorsAnalyzed, setCompetitorsAnalyzed] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("Researching latest trends...");
  const [tiktokPosts, setTiktokPosts] = useState([]);
  const [musicData, setMusicData] = useState([]);
  const [reelsNiche, setReelsNiche] = useState("");
  const [tiktokNiche, setTiktokNiche] = useState("");  
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || "videos";
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeTab);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, [activeTab]);

  // prevent trendingendpoint from triggering twice upon page load
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      getTrending(hashtag);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "tiktoks" && tiktokPosts.length === 0) {
      getTrendingTikToks();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "songs" && musicData.length === 0) {
      getTrendingTikToks();
    }
  }, [activeTab]);

  useEffect(() => {
    if (generating) {
      let messages;
      if (activeTab === "songs") {
        messages = [
          "Analyzing trending sounds...",
          "Processing viral music...",
          "Finding popular tracks...",
          "Gathering audio data...",
          "Identifying hit songs..."
        ];
      } else {
        messages = [
          "Researching latest trends...",
          "Analyzing relevant content...",
          "Processing viral patterns...",
          "Gathering engagement data...",
          "Filtering top performers..."
        ];
      }
      
      let currentIndex = 0;
      const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % messages.length;
        setLoadingMessage(messages[currentIndex]);
      }, 4000);
  
      return () => clearInterval(interval);
    }
  }, [generating, activeTab]);

const handleSort = (field, postType = 'reels') => {
  const newDirection =
    sortField === field && sortDirection === 'desc' ? 'asc' : 'desc';
  setSortField(field);
  setSortDirection(newDirection);

  let items = [];
  if (postType === 'reels') {
    items = [...posts];
  } else if (postType === 'tiktok') {
    items = [...tiktokPosts];
  } else if (postType === 'songs') {
    items = [...musicData];
  }

  const sortedItems = items.sort((a, b) => {
    let compareA = a[field];
    let compareB = b[field];

    if (field === 'upload_date') {
      compareA = new Date(a.upload_date * 1000);
      compareB = new Date(b.upload_date * 1000);
    }

    if (compareA < compareB) return newDirection === 'asc' ? -1 : 1;
    if (compareA > compareB) return newDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (postType === 'reels') {
    setPosts(sortedItems);
  } else if (postType === 'tiktok') {
    setTiktokPosts(sortedItems);
  } else if (postType === 'songs') {
    setMusicData(sortedItems);
  }
};

  // trending Instagram reels
  const getTrending = async (hashtag, niche = "") => {
    try {
      setGenerating(true);
      setPosts([]);

      const response = await axios.post(
        "https://api.viralgpt.com/trending",
        { mode, hashtag: hashtag, niche: niche },
        { headers: { "Content-Type": "application/json" } }
      );

      setPosts(
        response.data.trending.map((post) => ({
          ...post,
          isExpanded: false,
        }))
      );
      console.log("Server response:", response.data);
    } catch (e) {
      alert("An error occurred while fetching data: " + e.response.data.error);
    } finally {
      setGenerating(false);
    }
  };

  // trending TikToks
  const getTrendingTikToks = async (niche = "") => {
    try {
      setGenerating(true);
      setTiktokPosts([]);
      setMusicData([]); // Reset music data
  
      const response = await axios.get(
        "https://api.viralgpt.com/trending-tiktoks",
        { params: { niche } }
      );
  
      // Process TikTok posts as before
      const trendingData = response.data?.trending || [];
      // Deduplicate posts by video_url to avoid duplicates
      const uniqueTrendingData = Array.from(
        new Map(trendingData.map(item => [item.video_url, item])).values()
      );
  
      setTiktokPosts(
        uniqueTrendingData.map((post) => ({
          ...post,
          isExpanded: false,
          caption: post.caption || '',
          thumbnail_url: post.thumbnail_url || '',
          like_count: post.like_count || 0,
          comment_count: post.comment_count || 0,
          play_count: post.play_count || 0,
          upload_date: post.upload_date || Math.floor(Date.now() / 1000)
        }))
      );
  
      // Process music data
      const uniqueMusicMap = new Map();
      uniqueTrendingData.forEach(post => {
        if (post.music && post.music_info) {
          const musicId = post.music;
          if (!uniqueMusicMap.has(musicId)) {
            uniqueMusicMap.set(musicId, {
              id: musicId,
              title: post.music_info.title || 'Unknown',
              author: post.music_info.author || 'Unknown Artist',
              play_url: post.music_info.play || '',
              cover_url: post.music_info.cover || '',
              duration: post.music_info.duration || 0,
              is_original: post.music_info.original || false,
              play_count: post.play_count || 0,
              upload_date: post.upload_date || Math.floor(Date.now() / 1000)
            });
          } else {
            // Sum up play counts for same music
            const existing = uniqueMusicMap.get(musicId);
            existing.play_count += post.play_count || 0;
            existing.upload_date = Math.min(existing.upload_date, post.upload_date || Math.floor(Date.now() / 1000));
          }
        }
      });
  
      // Convert map to array and sort by play count
      const sortedMusicData = Array.from(uniqueMusicMap.values())
        .sort((a, b) => b.play_count - a.play_count);
      
      setMusicData(sortedMusicData);
      
    } catch (e) {
      console.error("TikTok fetch error:", e);
      setTiktokPosts([]);
      setMusicData([]);
      alert("An error occurred while fetching TikTok data: " + (e.response?.data?.error || e.message));
    } finally {
      setGenerating(false);
    }
  };

  // Spy on Competitors
  const fetchCompetitors = async () => {
    if (!spyUsername) {
      alert('Please enter an Instagram username');
      return;
    }
    
    setSpyLoading(true);
    setCompetitorsAnalyzed(0);

    const counterInterval = setInterval(() => {
      const volatility = Math.random();
      const increment = volatility > 0.8 
        ? Math.floor(Math.random() * 500 + 200)
        : Math.floor(Math.random() * 15 + 5);  
      
      setCompetitorsAnalyzed(prev => prev + increment);
    }, Math.floor(Math.random() * 200 + 50));

    try {
      const response = await axios.post("https://api.viralgpt.com/spy", {
        username: spyUsername
      });
      setCompetitors(response.data.competitors);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to fetch competitors');
    } finally {
      clearInterval(counterInterval);
      setSpyLoading(false);
      setCompetitorsAnalyzed(0);
    }
  };

  // Toggle caption for Instagram reels
  const toggleCaption = (index) => {
    setPosts((prevPosts) =>
      prevPosts.map((post, i) =>
        i === index ? { ...post, isExpanded: !post.isExpanded } : post
      )
    );
  };

  // Toggle caption for TikToks
  const toggleTiktokCaption = (index) => {
    setTiktokPosts((prevPosts) =>
      prevPosts.map((post, i) =>
        i === index ? { ...post, isExpanded: !post.isExpanded } : post
      )
    );
  };

  return (
    <Container className="py-4">
      <h1 className="text-4xl font-bold text-gray-800">
        <span className="text-instagram">ViralGPT</span> Charts
      </h1>
      <p className="text-muted">
        Browse the top trends on Instagram by audio and video.
        <br />
        <strong>Note:</strong> Data resets every 24 hours.
      </p>

      <Tab.Container 
        defaultActiveKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
      >
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="videos">Trending Reels</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="tiktoks">Trending TikToks</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="songs">Trending Audio</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="videos" className="scrollable-container">
            <p className="text-muted mb-2">
              Reveal the most relevant trends specific to your niche
            </p>
            <div className="mb-3 d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Enter your niche"
                value={reelsNiche}
                onChange={(e) => setReelsNiche(e.target.value)}
                style={{ maxWidth: '300px' }}
              />
              <Button 
                onClick={() => getTrending(hashtag, reelsNiche)}
                disabled={generating}
                className="bg-instagram"
              >
                Search Niche
              </Button>
              <Form.Select 
                onChange={(e) => handleSort(e.target.value)}
                style={{ maxWidth: '200px' }}
              >
                <option value="">Sort by...</option>
                <option value="like_count">Likes</option>
                <option value="comment_count">Comments</option>
                <option value="play_count">Views</option>
                <option value="upload_date">Date</option>
              </Form.Select>
            </div>
            {posts.length > 0 ? (
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Thumbnail</th>
                    <th>Caption</th>
                    <th>Stats</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post, index) => (
                    <tr key={post.id || index}>
                      <td>{index + 1}</td>
                      <td>
                        <img
                          src={post.thumbnail_url}
                          alt="thumbnail"
                          style={{ width: "300px", height: "auto" }}
                        />
                      </td>
                      <td>
                        {post.caption.length > 100 ? (
                          <>
                            <p>{post.isExpanded ? post.caption : `${post.caption.substring(0, 100)}...`}</p>
                            <span
                              onClick={() => toggleCaption(index)}
                              style={{ color: "blue", cursor: "pointer" }}
                            >
                              {post.isExpanded ? "Show Less" : "Show More"}
                            </span>
                          </>
                        ) : (
                          <p>{post.caption}</p>
                        )}
                      </td>
                      <td>
                        <i className="fa-solid fa-heart"></i> {post.like_count}
                        <br />
                        <i className="fa-solid fa-comment"></i> {post.comment_count}
                        <br />
                        <i className="fa-solid fa-play"></i> {post.play_count}
                        <br />
                        <i className="fa-solid fa-calendar"></i> {new Date(post.upload_date * 1000).toLocaleDateString()}
                      </td>
                      <td>
                        <a href={`https://instagram.com/p/${post.trending}`} target="_blank" rel="noopener noreferrer">
                          Open
                        </a>
                        <br />
                        <a download href={post.thumbnail_url}>Download</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : generating ? null : (
              <p className="text-center text-muted mt-4">No results found</p>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="tiktoks" className="scrollable-container">
            <p className="text-muted mb-2">
              Reveal the most relevant trends specific to your niche
            </p>
            <div className="mb-3 d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Enter your niche"
                value={tiktokNiche}
                onChange={(e) => setTiktokNiche(e.target.value)}
                style={{ maxWidth: '300px' }}
              />
              <Button 
                onClick={() => getTrendingTikToks(tiktokNiche)}
                disabled={generating}
                className="bg-instagram"
              >
                Search Niche
              </Button>
              <Form.Select 
                onChange={(e) => handleSort(e.target.value, 'tiktok')}
                style={{ maxWidth: '200px' }}
              >
                <option value="">Sort by...</option>
                <option value="like_count">Likes</option>
                <option value="comment_count">Comments</option>
                <option value="play_count">Views</option>
                <option value="upload_date">Date</option>
              </Form.Select>
            </div>
            {tiktokPosts.length > 0 ? (
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Thumbnail</th>
                    <th>Caption</th>
                    <th>Stats</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tiktokPosts.map((post, index) => (
                    <tr key={post.video_url || index}>
                      <td>{index + 1}</td>
                      <td>
                        <img
                          src={post.thumbnail_url}
                          alt="thumbnail"
                          style={{ width: "300px", height: "auto" }}
                        />
                      </td>
                      <td>
                        {post.caption.length > 100 ? (
                          <>
                            <p>{post.isExpanded ? post.caption : `${post.caption.substring(0, 100)}...`}</p>
                            <span
                              onClick={() => toggleTiktokCaption(index)}
                              style={{ color: "blue", cursor: "pointer" }}
                            >
                              {post.isExpanded ? "Show Less" : "Show More"}
                            </span>
                          </>
                        ) : (
                          <p>{post.caption}</p>
                        )}
                      </td>
                      <td>
                        <i className="fa-solid fa-heart"></i> {post.like_count}
                        <br />
                        <i className="fa-solid fa-comment"></i> {post.comment_count}
                        <br />
                        <i className="fa-solid fa-play"></i> {post.play_count}
                        <br />
                        <i className="fa-solid fa-calendar"></i> {new Date(post.upload_date * 1000).toLocaleDateString()}
                      </td>
                      <td>
                        <a href={post.video_url} target="_blank" rel="noopener noreferrer">
                          Open
                        </a>
                        <br />
                        <a download href={post.thumbnail_url}>Download</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : generating ? null : (
              <p className="text-center text-muted mt-4">No results found</p>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="songs" className="scrollable-container">
            <div className="mb-3 d-flex gap-2">
              <Form.Select 
                onChange={(e) => handleSort(e.target.value, 'songs')}
                style={{ maxWidth: '200px' }}
              >
                <option value="">Sort by...</option>
                <option value="play_count">Plays</option>
                <option value="upload_date">Date</option>
              </Form.Select>
            </div>
            {musicData.length > 0 ? (
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Title</th>
                    <th>Artist</th>
                    <th>Cover</th>
                    <th>Plays</th>
                    <th>Preview</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {musicData.map((music, index) => (
                    <tr key={music.id}>
                      <td>{index + 1}</td>
                      <td>{music.title}</td>
                      <td>{music.author}</td>
                      <td>
                        {music.cover_url && (
                          <img 
                            src={music.cover_url} 
                            alt="cover" 
                            style={{ width: "50px", height: "50px", objectFit: "cover" }}
                          />
                        )}
                      </td>
                      <td>{music.play_count.toLocaleString()}</td>
                      <td>
                        {music.play_url && (
                          <audio controls style={{ height: "30px" }}>
                            <source src={music.play_url} type="audio/mp4" />
                            Your browser does not support the audio element.
                          </audio>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${music.is_original ? 'bg-success' : 'bg-primary'}`}>
                          {music.is_original ? 'Original Sound' : 'Music Track'}
                        </span>
                      </td>
                      <td>{new Date(music.upload_date * 1000).toLocaleDateString()}</td>
                      <td className="text-center">
                        {music.play_url && (
                          <a 
                            href={music.play_url} 
                            download={`${music.title} - ${music.author}.mp4`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="fas fa-download"></i>
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : generating ? null : (
              <p className="text-center text-muted mt-4">No results found</p>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="spy">
            <div className="mb-4">
              <p className="text-muted">Enter an Instagram username to find likeminded profiles to connect with.</p>
              <div className="d-flex gap-2 mb-4">
                <Form.Control
                  type="text"
                  placeholder="Username"
                  value={spyUsername}
                  onChange={(e) => setSpyUsername(e.target.value)}
                  style={{ maxWidth: '250px' }}
                />
                <Button 
                  className={spyLoading ? "disabled bg-instagram" : "bg-instagram"}
                  onClick={fetchCompetitors}
                >
                  {spyLoading 
                    ? `Analyzing... (${competitorsAnalyzed.toLocaleString()} competitors processed)` 
                    : "Analyze"}
                </Button>
                <div 
                  style={{fontSize: "1.5rem"}} 
                  className={`ms-2 fa fa-circle-notch fa-spin text-instagram ${!spyLoading ? "d-none" : ""}`}
                />
              </div>
              <div className="row">
              {competitors.map((competitor, index) => (
                <div key={index} className="col-md-4 mb-4">
                  <a 
                    href={`https://instagram.com/${competitor.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="border rounded p-3 hover-shadow" style={{ 
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer'
                    }}>
                      <div className="d-flex align-items-center mb-3">
                        <img 
                          src={`https://rizzup.net/proxy/${competitor.profile_pic_url}`} 
                          alt={competitor.username}
                          className="rounded-circle"
                          style={{ width: "50px", height: "50px", objectFit: "cover" }}
                        />
                        <div className="ms-3">
                          <h6 className="mb-0 d-flex align-items-center">
                            {competitor.username}
                            {competitor.is_verified && 
                              <i className="fas fa-check-circle text-primary ms-1" style={{fontSize: "14px"}}/>
                            }
                            <span className="ms-2 badge bg-instagram" title="Similarity Score">
                              {competitor.similarity_score}%
                            </span>
                          </h6>
                          <small className="text-muted">{competitor.full_name}</small>
                        </div>
                      </div>
                      
                      <p className="text-muted small mb-0" style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: "2",
                        WebkitBoxOrient: "vertical"
                      }}>
                        {competitor.biography}
                      </p>
                    </div>
                  </a>
                </div>
              ))}
              </div>
            </div>
          </Tab.Pane>
        </Tab.Content>
        {generating && (activeTab === "videos" || activeTab === "tiktoks" || activeTab === "songs") && (
          <div className="text-center mt-4 d-flex align-items-center justify-content-center">
            <span>{loadingMessage}</span>
            <div className="ms-2 fa fa-circle-notch fa-spin text-instagram"/>
          </div>
        )}
      </Tab.Container>
    </Container>
  );
};

export default Trending;
